import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RowSelectedEvent } from 'ag-grid-community';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { LoadingMask } from 'src/app/classes/loading-mask';
import {
  Company,
  CompanyComplianceType,
  ExemptVendorType
} from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import {
  ApConfigService,
  buildApConfigSelectionMap
} from 'src/app/services/ap-config.service';
import { CompanyService } from 'src/app/services/company.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import {
  ApVendorsMapToExemptVendorResponseDto,
  ExemptVendorsCreateRequestDto
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

import { AddEditExemptVendorModalComponent } from '../add-edit-exempt-vendor-modal/add-edit-exempt-vendor-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

declare type ExemptVendorUpload = ExemptVendorsCreateRequestDto &
  Pick<
    ApVendorsMapToExemptVendorResponseDto,
    | 'ApVendors'
    | 'ExemptVendorType'
    | 'hasDuplicateInfo'
    | 'hasMissingRequiredFields'
  > & {
    CompanyVendors: (ApVendorsMapToExemptVendorResponseDto['CompanyVendors'][0] & {
      forceTempActive?: boolean;
      forceTempActiveTime?: number;
    })[];
    CompanyComplianceTypeId: number;
    forceTempActiveTime: number;
    forceActiveCounter: number;
    hasSharedTaxId?: boolean;
    taxId?: string;
    rowId?: string;
  };

@Component({
  selector: 'app-exempt-vendors-upload-modal',
  templateUrl: './exempt-vendors-upload-modal.component.html',
  styleUrl: './exempt-vendors-upload-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ExemptVendorsUploadModalComponent
  implements OnInit, AfterViewInit
{
  constructor(
    private activeModal: NgbActiveModal,
    private ModalService: NgbModal,
    private ToastService: ToastService,
    private HostElement: ElementRef,
    private ExemptVendorService: ExemptVendorService,
    private CompanyService: CompanyService,
    private ApConfigService: ApConfigService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {}

  @Input() ExemptVendors?: ExemptVendorUpload[];
  @Input() CompanyId?: number;
  @Input() selectedApConfigIds?: number[];

  private readonly taxIdFields = ['ein', 'ssn', 'bsn'] as const;

  Company?: Company;

  title: string = 'Exempt Vendors Upload';
  duplicateVendorCount: number = 0;
  missingRequiredFieldsCount: number = 0;
  sharedTaxIdVendorCount: number = 0;
  ExemptVendorTypes: ExemptVendorType[] = [];
  selectedExemptVendorType?: ExemptVendorType;
  CompanyComplianceTypes: CompanyComplianceType[] = [];
  selectedCompanyComplianceType?: CompanyComplianceType;
  selectedRows: ExemptVendorUpload[] = [];

  apConfigSelectionMap: Map<string, number> = new Map();
  selectedApConfigIdsMap: Map<string, number> = new Map();

  private rowDataSubject = new BehaviorSubject<any[]>([]);
  rowData: Observable<any[]> = this.rowDataSubject.asObservable();
  rowClassRules = {
    'gridRow-yellow': (params: any) => params.data.hasDuplicateInfo === true,
    'gridRow-red': (params: any) =>
      params.data.hasMissingRequiredFields === true,
    'gridRow-pink': (params: any) => params.data.hasSharedTaxId === true
  };
  reloadGrid: boolean = false;
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'delete']
  };

  apConfigSelectionChange(selectedItems: Map<string, number>): void {
    this.selectedApConfigIdsMap = selectedItems;
  }

  private loadCreateEligibleApConfigs(): void {
    this.ApConfigService.getCreateEligibleApConfigs(this.CompanyId).subscribe({
      next: (apConfigs) => {
        this.apConfigSelectionMap = buildApConfigSelectionMap(apConfigs);

        if (this.selectedApConfigIds?.length) {
          const selectedIds = new Set(this.selectedApConfigIds);
          this.apConfigSelectionMap = new Map(
            Array.from(this.apConfigSelectionMap.entries()).filter(([, id]) =>
              selectedIds.has(id)
            )
          );
          this.selectedApConfigIdsMap = new Map(this.apConfigSelectionMap);
        }

        // Zoneless: flush the async result to the view.
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  private loadExemptVendorTypes(): void {
    this.ExemptVendorService.getExemptVendorTypes({
      CompanyId: this.CompanyId
    }).subscribe({
      next: (exemptVendorTypes) => {
        this.ExemptVendorTypes.splice(
          0,
          this.ExemptVendorTypes.length,
          ...exemptVendorTypes
        );
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  private loadCompanyComplianceTypes(): void {
    this.CompanyService.getCompanyComplianceTypes({
      CompanyId: this.CompanyId
    }).subscribe({
      next: (companyComplianceTypes) => {
        this.CompanyComplianceTypes.splice(
          0,
          this.CompanyComplianceTypes.length,
          ...companyComplianceTypes
        );
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  rowSelectionChanged(event: RowSelectedEvent) {
    const selectedRows = event.api.getSelectedRows();
    this.selectedRows.splice(0, this.selectedRows.length, ...selectedRows);
  }

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  upload() {
    if (!this.ExemptVendors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload ExemptVendors. There are no ExemptVendors to upload.',
        { returnError: true }
      );
    }

    this.ExemptVendors = this.ExemptVendors.filter(
      (vendor: any) => !vendor.hasMissingRequiredFields
    );

    if (this.ExemptVendors.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload ExemptVendors. There are no ExemptVendors to upload.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Uploading ExemptVendors...'
    });

    this.ExemptVendors = this.ExemptVendors.map((ev) => {
      const { CompanyVendors } = ev;

      if (!CompanyVendors || (CompanyVendors && CompanyVendors.length !== 1)) {
        mask.destroy();
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot upload clientRegisteredVendors. Missing companyVendor record.',
          { returnError: true }
        );
      }

      const { CompanyComplianceType, forceTempActive, forceTempActiveTime } =
        CompanyVendors[0];

      if (
        !CompanyComplianceType ||
        (CompanyComplianceType && !CompanyComplianceType.id)
      ) {
        mask.destroy();
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot upload clientRegisteredVendors. Missing Company Compliance type.',
          { returnError: true }
        );
      }

      return Object.assign(ev, {
        CompanyComplianceTypeId: CompanyComplianceType.id,
        ...(forceTempActive !== undefined && { forceTempActive }),
        ...(forceTempActiveTime !== undefined && { forceTempActiveTime })
      });
    });

    const selectedApConfigIds = Array.from(
      this.selectedApConfigIdsMap.values()
    );

    from(
      companyVendorsService.createManyNewExemptVendors({
        pathParams: { companyId: this.CompanyId! },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS },
        data: {
          items: this.ExemptVendors.map((exemptVendor) => {
            const {
              ApVendors,
              CompanyComplianceTypeId,
              forceTempActiveTime,
              forceActiveCounter
            } = exemptVendor;

            const apVendorId =
              ApVendors && ApVendors.length === 1 && ApVendors[0].id;

            return {
              ...(selectedApConfigIds &&
                selectedApConfigIds.length > 0 && {
                  apConfigIds: selectedApConfigIds
                }),
              ...(apVendorId && { apVendorIdToLink: apVendorId }),
              CompanyComplianceTypeId,
              forceTempActiveTime,
              forceActiveCounter,
              exemptVendor: {
                legalName: exemptVendor.legalName,
                accountingId: exemptVendor.accountingId,
                ExemptVendorTypeId: exemptVendor.ExemptVendorTypeId,
                dba: exemptVendor.dba,
                ein: exemptVendor.ein,
                ssn: exemptVendor.ssn,
                bsn: exemptVendor.bsn,
                phone: exemptVendor.phone,
                phone2: exemptVendor.phone2,
                fax: exemptVendor.fax,
                ...(exemptVendor.usesSIN !== undefined && {
                  usesSIN: exemptVendor.usesSIN
                }),
                ...(exemptVendor.website && { website: exemptVendor.website }),
                ...(exemptVendor.companyType && {
                  companyType: exemptVendor.companyType
                }),
                ...(exemptVendor.jurisdiction && {
                  jurisdiction: exemptVendor.jurisdiction
                }),
                ...(exemptVendor.llcType && { llcType: exemptVendor.llcType }),
                ...(exemptVendor.isOnSite !== undefined && {
                  isOnSite: exemptVendor.isOnSite
                }),
                ...(exemptVendor.isProfessional !== undefined && {
                  isProfessional: exemptVendor.isProfessional
                }),
                ...(exemptVendor.isSupplier !== undefined && {
                  isSupplier: exemptVendor.isSupplier
                }),
                ...(exemptVendor.is1099Issued !== undefined && {
                  is1099Issued: exemptVendor.is1099Issued
                }),
                ...(exemptVendor.StreetAddress && {
                  StreetAddress: {
                    address1: exemptVendor.StreetAddress.address1 ?? null,
                    address2: exemptVendor.StreetAddress.address2 ?? null,
                    city: exemptVendor.StreetAddress.city ?? null,
                    state: exemptVendor.StreetAddress.state ?? null,
                    zip: exemptVendor.StreetAddress.zip ?? null,
                    country: exemptVendor.StreetAddress.country ?? null
                  }
                }),
                ...(exemptVendor.MailingAddress && {
                  MailingAddress: {
                    address1: exemptVendor.MailingAddress.address1 ?? null,
                    address2: exemptVendor.MailingAddress.address2 ?? null,
                    city: exemptVendor.MailingAddress.city ?? null,
                    state: exemptVendor.MailingAddress.state ?? null,
                    zip: exemptVendor.MailingAddress.zip ?? null,
                    country: exemptVendor.MailingAddress.country ?? null
                  }
                }),
                Contacts: (exemptVendor.Contacts ?? []).map((contact) => {
                  const {
                    email,
                    firstName,
                    lastName,
                    jobTitle,
                    phone1,
                    phone2,
                    ext,
                    ext2,
                    isCompliance,
                    isPrimaryCompliance
                  } = contact;

                  return {
                    ...(email && { email }),
                    ...(firstName && { firstName }),
                    ...(lastName && { lastName }),
                    ...(jobTitle && { jobTitle }),
                    ...(phone1 && { phone1 }),
                    ...(phone2 && { phone2 }),
                    ...(ext && { ext }),
                    ...(ext2 && { ext2 }),
                    ...(isCompliance !== undefined && { isCompliance }),
                    ...(isPrimaryCompliance !== undefined && {
                      isPrimaryCompliance
                    })
                  };
                })
              }
            };
          })
        }
      })
    ).subscribe({
      next: () => {
        mask.destroy();
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully uploaded ExemptVendors.'
        );
        this.activeModal.close();
      },
      error: () => mask.destroy()
    });
  }

  actionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (action === 'edit') {
      if (!this.CompanyId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot edit vendor. Missing Company ID.',
          { returnError: true }
        );
      }

      const modalRef = this.ModalService.open(
        AddEditExemptVendorModalComponent,
        { centered: true, size: 'lg' }
      );
      Object.assign(modalRef.componentInstance, {
        ExemptVendor: rowData,
        CompanyId: this.CompanyId
      });

      modalRef.closed.subscribe({
        next: () => {
          Object.assign(this.ExemptVendors!, rowData);

          this.reloadGrid = true;
          this.calculateCounts(this.ExemptVendors!);
          this.ChangeDetectorRef.markForCheck();
        }
      });
    }

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );
      Object.assign(componentInstance, {
        displayText:
          'Are you sure you want to remove this vendor from the upload?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            const index = this.ExemptVendors?.findIndex(
              (vendor) => vendor === rowData
            );

            if (index !== -1) {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully removed vendor from upload.'
              );
              this.ExemptVendors?.splice(index!, 1);
              this.rowDataSubject.next([...this.ExemptVendors!]);
              this.calculateCounts(this.ExemptVendors!);
              this.reloadGrid = true;
              this.ChangeDetectorRef.markForCheck();
            } else {
              this.ToastService.addErrorToast(
                'Error',
                'No vendor found to remove.'
              );
            }
          }
        }
      });
    }
  }

  applyDataToSelectedRows(): void {
    if (!this.selectedCompanyComplianceType && !this.selectedExemptVendorType) {
      this.ToastService.addErrorToast(
        'Error',
        'Please select a compliance type or exempt vendor type before applying it to the selected rows.'
      );
      return;
    }

    for (const row of this.selectedRows) {
      if (this.selectedCompanyComplianceType) {
        for (const companyVendor of row.CompanyVendors ?? []) {
          Object.assign(companyVendor, {
            CompanyComplianceType: this.selectedCompanyComplianceType
          });
        }

        row.CompanyComplianceTypeId = this.selectedCompanyComplianceType.id!;
      }

      if (this.selectedExemptVendorType) {
        row.ExemptVendorTypeId = this.selectedExemptVendorType.id!;
      }
    }

    this.reloadGrid = true;

    this.ToastService.addSuccessToast(
      'Success',
      `Successfully applied changes to ${this.selectedRows.length} vendor${this.selectedRows.length !== 1 ? 's' : ''}.`
    );
  }

  applyComplianceTypeToSelectedRows(): void {
    if (!this.selectedCompanyComplianceType) {
      this.ToastService.addErrorToast(
        'Error',
        'Please select a compliance type before applying it to the selected rows.'
      );
      return;
    }

    for (const row of this.selectedRows) {
      for (const companyVendor of row.CompanyVendors ?? []) {
        Object.assign(companyVendor, {
          CompanyComplianceType: this.selectedCompanyComplianceType
        });
      }

      row.CompanyComplianceTypeId = this.selectedCompanyComplianceType.id!;
    }

    this.reloadGrid = true;

    this.ToastService.addSuccessToast(
      'Success',
      `Successfully applied ${this.selectedCompanyComplianceType.type} to ${this.selectedRows.length} vendor${this.selectedRows.length !== 1 ? 's' : ''}.`
    );
  }

  removeRedRows() {
    if (!this.ExemptVendors) {
      return;
    }

    const originalCount = this.ExemptVendors.length;
    this.ExemptVendors = this.ExemptVendors.filter(
      (vendor: any) => !vendor.hasMissingRequiredFields
    );

    const removedCount = originalCount - this.ExemptVendors.length;
    if (removedCount > 0) {
      this.ToastService.addSuccessToast(
        'Success',
        `Successfully removed ${removedCount} vendor${removedCount !== 1 ? 's' : ''} with missing required fields.`
      );
      this.rowDataSubject.next([...this.ExemptVendors]);
      this.calculateCounts(this.ExemptVendors);
      this.reloadGrid = true;
      this.ChangeDetectorRef.markForCheck();
    }
  }

  removeYellowRows() {
    if (!this.ExemptVendors) {
      return;
    }

    const originalCount = this.ExemptVendors.length;
    this.ExemptVendors = this.ExemptVendors.filter(
      (vendor: any) => !vendor.hasDuplicateInfo
    );

    const removedCount = originalCount - this.ExemptVendors.length;
    if (removedCount > 0) {
      this.ToastService.addSuccessToast(
        'Success',
        `Successfully removed ${removedCount} duplicate vendor${removedCount !== 1 ? 's' : ''}.`
      );
      this.rowDataSubject.next([...this.ExemptVendors]);
      this.calculateCounts(this.ExemptVendors);
      this.reloadGrid = true;
      this.ChangeDetectorRef.markForCheck();
    }
  }

  removePinkRows() {
    if (!this.ExemptVendors) {
      return;
    }

    const originalCount = this.ExemptVendors.length;
    this.ExemptVendors = this.ExemptVendors.filter(
      (vendor) => !vendor.hasSharedTaxId
    );

    const removedCount = originalCount - this.ExemptVendors.length;
    if (removedCount > 0) {
      this.ToastService.addSuccessToast(
        'Success',
        `Successfully removed ${removedCount} vendor${removedCount !== 1 ? 's' : ''} sharing a tax id with another vendor in the upload.`
      );
      this.rowDataSubject.next([...this.ExemptVendors]);
      this.calculateCounts(this.ExemptVendors);
    }
  }

  calculateCounts(rows: any[]) {
    if (!rows) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot calculate counts. No ExemptVendor data found.',
        { returnError: true }
      );
    }

    this.duplicateVendorCount = rows.filter(
      (row: any) => row.hasDuplicateInfo === true
    ).length;

    this.missingRequiredFieldsCount = rows.filter(
      (row: any) => row.hasMissingRequiredFields === true
    ).length;

    this.sharedTaxIdVendorCount = this.markSharedTaxIdRows(
      rows as ExemptVendorUpload[]
    );
  }

  private markSharedTaxIdRows(rows: ExemptVendorUpload[]): number {
    const taxIdCounts = {
      ein: new Map<string, number>(),
      ssn: new Map<string, number>(),
      bsn: new Map<string, number>()
    };

    for (const row of rows) {
      for (const field of this.taxIdFields) {
        const taxId = this.stripTaxId(row[field]);

        if (taxId) {
          taxIdCounts[field].set(
            taxId,
            (taxIdCounts[field].get(taxId) || 0) + 1
          );
        }
      }
    }

    let sharedTaxIdRowCount = 0;

    for (const row of rows) {
      row.hasSharedTaxId = this.taxIdFields.some((field) => {
        const taxId = this.stripTaxId(row[field]);

        return !!taxId && (taxIdCounts[field].get(taxId) || 0) > 1;
      });

      if (row.hasSharedTaxId) {
        sharedTaxIdRowCount++;
      }
    }

    return sharedTaxIdRowCount;
  }

  private stripTaxId(taxId?: string | null): string {
    return taxId ? taxId.replace(/[^0-9]/g, '') : '';
  }

  ngOnInit(): void {
    if (!this.ExemptVendors || this.ExemptVendors.length === 0) {
      this.activeModal.close();
      setTimeout(() => {
        this.ToastService.addErrorToast(
          'Error',
          'Cannot upload ExemptVendors. No ExemptVendors found.'
        );
        this.ChangeDetectorRef.detectChanges();
      });
      this.rowDataSubject.next([]);
      return;
    }

    this.ExemptVendors = this.ExemptVendors.map((exemptVendor) => ({
      ...exemptVendor,
      taxId: exemptVendor.ein || exemptVendor.ssn || exemptVendor.bsn || '',
      rowId: crypto.randomUUID()
    }));

    this.rowDataSubject.next([...this.ExemptVendors]);

    this.calculateCounts(this.ExemptVendors);
    this.loadCreateEligibleApConfigs();
    this.loadExemptVendorTypes();
    this.loadCompanyComplianceTypes();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.ChangeDetectorRef.detectChanges();
    }, 150);
  }
}
